import { BaseError } from 'viem';
import { z } from 'zod';

const messages = {
  'api-origin-unavailable': 'API origin unavailable',
  'http-refused': 'HTTP request refused',
  'request-timeout': 'request timed out',
  'response-body-timeout': 'response body timed out',
  'request-failed': 'request transport failed',
  'response-body-failed': 'response body read failed',
  'response-invalid': 'response invalid',
  'read-budget': 'preparation read budget exhausted',
  'preparation-deadline': 'preparation deadline reached',
  'context-refused': 'captured context refused',
  'method-refused': 'method refused',
  'request-too-large': 'request size refused',
} as const;
export type TerminalPreparationCode = keyof typeof messages;
const route = z.enum(['chain', 'bundler', 'counterfactual', 'send']);
const method = z.enum(['eth_chainId', 'eth_blockNumber', 'eth_getBlockByNumber', 'eth_getBlockByHash',
  'eth_getCode', 'eth_getBalance', 'eth_getTransactionCount', 'eth_getStorageAt', 'eth_call',
  'eth_estimateUserOperationGas', 'eth_sendUserOperation']);
export type TerminalPreparationContext = { route?: z.infer<typeof route>; method?: string };
const milliseconds = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const count = z.number().int().min(0).max(384);
const progressSchema = z.strictObject({
  elapsedMs: milliseconds, issued: count, finished: count, aggregateRequestWaitMs: milliseconds,
  slowest: z.array(z.strictObject({ route: route.optional(), method: method.optional(), calls: count,
    milliseconds })).max(3),
}).refine(value => value.finished <= value.issued
  && value.slowest.every(row => row.calls <= value.finished && row.milliseconds <= value.aggregateRequestWaitMs));
type Diagnostic = Readonly<{ code: TerminalPreparationCode; route?: z.infer<typeof route>;
  method?: z.infer<typeof method>; httpStatus?: number; progress?: z.infer<typeof progressSchema> }>;
// A matching name/prototype/message is not a carrier. Only construction by this
// code records validated data; later message/property mutations cannot leak.
const diagnostics = new WeakMap<object, Diagnostic>();
function render(value: Diagnostic) {
  const label = value.code === 'http-refused' && value.httpStatus !== undefined
    ? `HTTP ${value.httpStatus} refused` : messages[value.code];
  const location = [value.route, value.method].filter(Boolean).join(' ');
  let text = `Terminal owner review connection unavailable: ${label}${location ? ` [${location}]` : ''}.`;
  if (value.progress) {
    const p = value.progress;
    const slowest = p.slowest.map(row => `${[row.route, row.method].filter(Boolean).join(' ')}: ${row.calls} calls/${row.milliseconds}ms`).join('; ');
    text += ` Preparation diagnostics: elapsed ${p.elapsedMs}ms; issued ${p.issued}; finished ${p.finished}; aggregate request-wait ${p.aggregateRequestWaitMs}ms${slowest ? `; ${slowest}` : ''}.`;
  }
  return text;
}

/** Diagnostic-only: no original cause, body, URL, parameters or authority. */
export class TerminalPreparationError extends Error {
  constructor(code: TerminalPreparationCode, context?: TerminalPreparationContext, status?: number) {
    const safeCode = Object.hasOwn(messages, code) ? code : 'request-failed';
    const safeRoute = route.safeParse(context?.route), safeMethod = method.safeParse(context?.method);
    const value: Diagnostic = Object.freeze({ code: safeCode,
      ...(safeRoute.success ? { route: safeRoute.data } : {}),
      ...(safeMethod.success ? { method: safeMethod.data } : {}),
      ...(Number.isInteger(status) && status! >= 100 && status! <= 599 ? { httpStatus: status } : {}) });
    super(render(value));
    diagnostics.set(this, value);
  }
  recordProgress(input: unknown) {
    const prior = diagnostics.get(this), parsed = progressSchema.safeParse(input);
    if (!prior || !parsed.success) return;
    const progress = { ...parsed.data, slowest: parsed.data.slowest.map(row => Object.freeze({ ...row })) };
    Object.freeze(progress.slowest); Object.freeze(progress);
    const value = Object.freeze({ ...prior, progress });
    diagnostics.set(this, value); this.message = render(value);
  }
}

/** Installed viem may wrap this error more than once. Walk only its actual
 * BaseError wrappers, finitely and without ever reading/rendering raw text.
 * Generic errors, counterfeit carriers, cycles and deeper chains stay opaque. */
export function terminalPreparationDiagnosticMessage(error: unknown): string | undefined {
  const seen = new Set<object>();
  try {
    for (let depth = 0; depth < 8 && typeof error === 'object' && error !== null; depth++) {
      if (seen.has(error)) return undefined;
      seen.add(error);
      const value = diagnostics.get(error);
      if (value) return render(value);
      if (!(error instanceof BaseError)) return undefined;
      error = error.cause;
    }
  } catch { /* A foreign getter cannot replace the original safe refusal. */ }
  return undefined;
}
